import { BRIEF_DRAFTING_QUERY_KEY } from "@entities/brief/lib/constants";
import { useMutation } from "@shared/lib/hooks";
import { ToastService } from "@shared/lib/services";
import {
  ICreateDraftComment,
  IDraftingComments,
  IDraftingResponse,
} from "@shared/lib/types/drafting";
import { BriefService } from "../../services/brief.service";
import { BackednResponse } from "@shared/lib/types";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@entities/auth";

export function useCreateDraftingComment(draftingId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const updateComment = (
    payload: ICreateDraftComment,
    status: string,
    data?: BackednResponse<{
      comment: IDraftingComments;
      clientCommentId?: string;
    }>
  ) => {
    queryClient.setQueryData(
      BRIEF_DRAFTING_QUERY_KEY(draftingId),
      (oldData: BackednResponse<IDraftingResponse>) => {
        return {
          ...oldData,
          data: {
            ...oldData.data,
            drafting: {
              ...oldData.data.drafting,
              draftingContents: oldData.data?.drafting?.draftingContents?.map(
                (content) => {
                  if (content._id === payload?.draftingVersionId) {
                    return {
                      ...content,
                      comments:
                        status === "pending"
                          ? [
                              ...(oldData?.data?.drafting?.draftingContents?.find(
                                (c) => c._id === payload?.draftingVersionId
                              )?.comments || []),
                              {
                                ...payload.comments,
                                commentedBy: {
                                  firstName:
                                    user?.displayName?.split(" ")[0] || "User",
                                  lastName:
                                    user?.displayName?.split(" ")[1] || "",
                                  profilePicUrl: user?.photoURL || "",
                                },
                                status: status,
                              },
                            ]
                          : status === "error"
                          ? (
                              oldData?.data?.drafting?.draftingContents?.find(
                                (c) => c._id === payload?.draftingVersionId
                              )?.comments || []
                            ).map((comment) => {
                              if (
                                comment.clientCommentId ===
                                payload.comments.clientCommentId
                              ) {
                                return {
                                  ...comment,
                                  commentedBy: {
                                    firstName:
                                      user?.displayName?.split(" ")[0] ||
                                      "User",
                                    lastName:
                                      user?.displayName?.split(" ")[1] || "",
                                    profilePicUrl: user?.photoURL || "",
                                  },
                                  status: status,
                                };
                              }
                              return { ...comment };
                            })
                          : (
                              oldData?.data?.drafting?.draftingContents?.find(
                                (c) => c._id === payload?.draftingVersionId
                              )?.comments || []
                            ).map((comment) => {
                              if (
                                comment.clientCommentId ===
                                payload.comments.clientCommentId
                              ) {
                                return {
                                  ...data?.data?.comment,
                                  commentedBy: {
                                    firstName:
                                      user?.displayName?.split(" ")[0] ||
                                      "User",
                                    lastName:
                                      user?.displayName?.split(" ")[1] || "",
                                    profilePicUrl: user?.photoURL || "",
                                  },
                                  status: status,
                                };
                              }
                              return { ...comment };
                            }),
                      updatedAt: new Date().toISOString(),
                    };
                  }
                  return content;
                }
              ),
            },
          },
        };
      }
    );
  };
  const {
    mutate: createComment,
    isLoading: isCreating,
    isSuccess: isCreated,
  } = useMutation({
    mutationFn: async (payload: ICreateDraftComment) => {
      updateComment(payload, "pending");
      return BriefService.createDraftingComment(payload).catch(async (err) => {
        const responseBody = await err.response?.json?.();
        if (responseBody?.msg) ToastService.error(responseBody.msg);
        updateComment(payload, "error");
        throw err;
      });
    },
    options: {
      skipInvalidation: true,
    },
    onSuccess: (c, data, params) => {
      updateComment(params, "success", data);
    },
  });

  return { createComment, isCreating, isCreated };
}
